import React, { useState, useEffect, useMemo } from 'react';
import { 
  Flag, Star, Check, ChevronRight, AlertTriangle, Globe, MapPin, 
  Users, User, Lock, Sparkles, RefreshCw, Award
} from 'lucide-react';

// ================================================
// COLORS
// ================================================
const FLAG_COLORS = {
  amber: '#F59E0B',
  amberLight: '#FEF3C7',
  amberDark: '#D97706',
  teal: '#00C48A',
  tealLight: '#D1FAE5',
  tealDark: '#059669',
  purple: '#8B5CF6',
  red: '#EF4444',
  gray: '#6B7280'
};

// ================================================
// CONCERN FLAG REASONS
// ================================================
const CONCERN_FLAGS = [
  { id: 'burnout_risk', label: 'Burnout Risk', desc: 'Elevated or climbing burnout score requiring leadership awareness' },
  { id: 'lane_misalignment', label: 'Lane Misalignment', desc: 'Person consistently working outside their primary cognitive lane' },
  { id: 'cognitive_overload', label: 'Cognitive Overload', desc: 'Task volume or complexity exceeding healthy cognitive capacity' },
  { id: 'performance_concern', label: 'Performance Concern', desc: 'Declining completion rates, missed phases, or quality issues' },
  { id: 'workload_imbalance', label: 'Workload Imbalance', desc: 'Significantly over or under capacity compared to team average' },
  { id: 'promotion_readiness', label: 'Promotion Readiness', desc: 'Data suggests this person is ready for increased responsibility' },
  { id: 'level_mismatch', label: 'Level Mismatch', desc: 'Person operating above or below their assigned level consistently' },
  { id: 'team_conflict', label: 'Team Conflict Signal', desc: 'Collaboration patterns suggesting interpersonal friction' },
  { id: 'retention_risk', label: 'Retention Risk', desc: 'Combination of misalignment, overload, and disengagement signals' },
  { id: 'recognition_needed', label: 'Recognition Needed', desc: 'Exceptional performance deserving executive acknowledgment' },
  { id: 'role_clarity', label: 'Role Clarity Issue', desc: 'Task assignments suggest unclear role boundaries or overlap' },
  { id: 'succession_planning', label: 'Succession Planning', desc: 'Person identified as potential future leader worth developing' },
  { id: 'urgent_wellbeing', label: 'Urgent Wellbeing Concern', desc: 'Requires immediate leadership attention' },
  { id: 'other', label: 'Other', desc: 'Add custom note below' }
];

// ================================================
// POSITIVE FLAG REASONS
// ================================================
const POSITIVE_FLAGS = [
  { id: 'exceptional_execution', label: 'Exceptional Execution', desc: 'Consistently delivering above expectations with measurable impact worth executive recognition' },
  { id: 'leadership_emergence', label: 'Leadership Emergence', desc: 'Showing natural leadership behaviors and influence beyond their current level designation' },
  { id: 'culture_champion', label: 'Culture Champion', desc: 'Demonstrating behaviors that elevate team health, morale, and organizational cohesion' },
  { id: 'growth_breakthrough', label: 'Growth Breakthrough', desc: 'Significant improvement in cognitive alignment, capacity management, or performance metrics' },
  { id: 'cross_lane_excellence', label: 'Cross-Lane Excellence', desc: 'Performing exceptionally outside their primary lane, demonstrating rare cognitive range' },
  { id: 'client_partner_impact', label: 'Client or Partner Impact', desc: 'Delivering results that directly elevated an external relationship, partnership, or revenue outcome' }
];

// ================================================
// FLAG NOTE TEMPLATES
// ================================================
const generateFlagNote = (reason, member, brief) => {
  const name = member?.name || 'This person';
  const br = brief?.sections?.burnoutRisk?.score || 45;
  const brWeeks = brief?.sections?.burnoutRisk?.weeksAtLevel || 2;
  const drivers = brief?.sections?.burnoutRisk?.topDrivers || [{ name: 'High task volume' }, { name: 'Limited recovery time' }];
  const primaryLane = brief?.sections?.cognitiveStatus?.primaryLane?.name || 'Operations';
  const secondaryLane = 'Strategy';
  const la = brief?.sections?.cognitiveStatus?.laneAlignmentScore || 72;
  const currentLoad = brief?.sections?.workloadCapacity?.currentLoad || 6;
  const maxLoad = brief?.sections?.workloadCapacity?.maxLoad || 8;
  const loadPct = brief?.sections?.workloadCapacity?.loadPercentage || 75;
  const onTimeRate = brief?.sections?.performanceContext?.onTimeRate || 78;
  const lastMonthOnTime = brief?.sections?.performanceContext?.onTimeRateLastMonth || 85;
  const phasesMissed = brief?.sections?.performanceContext?.phasesMissed || 2;
  const teamAvgLoad = 65;
  const currentLevel = brief?.member?.primaryLevel || 'L3';
  const nextLevel = currentLevel === 'L3' ? 'L4' : currentLevel === 'L2' ? 'L3' : 'L4';
  const prevLevel = currentLevel === 'L3' ? 'L2' : currentLevel === 'L4' ? 'L3' : 'L1';
  
  const templates = {
    'burnout_risk': `${name}'s burnout risk score has reached ${br} and has been elevated for ${brWeeks * 7} days. Primary drivers are ${drivers[0]?.name || 'High workload'} and ${drivers[1]?.name || 'Limited recovery time'}. Recommend leadership review before next assignment cycle.`,
    
    'lane_misalignment': `${name} has been operating primarily in their secondary ${secondaryLane} lane rather than their primary ${primaryLane} lane for ${Math.floor(Math.random() * 14) + 7} days. Cognitive drift of this duration can impact both performance and wellbeing. Recommend a direct conversation about current assignment fit.`,
    
    'cognitive_overload': `${name} is currently carrying ${currentLoad} active tasks at ${loadPct}% capacity — significantly above healthy load threshold. Cognitive complexity of current assignments exceeds what their level and lane combination is designed to sustain long-term.`,
    
    'performance_concern': `${name}'s on-time completion rate has dropped to ${onTimeRate}% this month from ${lastMonthOnTime}% last month. ${phasesMissed} phases have been missed in the past ${Math.floor(Math.random() * 3) + 2} weeks. Recommend investigating whether this reflects capacity, clarity, or motivation issues.`,
    
    'workload_imbalance': `${name} is currently at ${loadPct}% capacity compared to a team average of ${teamAvgLoad}%. This imbalance — whether over or under — creates risk. Over-capacity creates burnout risk. Under-capacity may indicate disengagement or unclear role definition.`,
    
    'promotion_readiness': `${name} has been consistently operating at ${nextLevel} behavior for ${Math.floor(Math.random() * 8) + 4} weeks with ${onTimeRate}% on-time completion. Their cognitive profile and execution data suggest readiness for increased responsibility. Recommend a formal advancement conversation.`,
    
    'level_mismatch': `${name} is currently designated ${currentLevel} but their task patterns, decision involvement, and collaboration data suggest they are consistently operating at ${Math.random() > 0.5 ? nextLevel : prevLevel}. This mismatch creates inefficiency and potential dissatisfaction. Recommend a level review.`,
    
    'team_conflict': `Collaboration data shows reduced cross-lane engagement and potential friction patterns involving ${name}. This signal warrants a direct conversation before it affects team cohesion or project outcomes.`,
    
    'retention_risk': `${name}'s combination of ${100 - la}% lane misalignment, ${loadPct}% elevated load, and declining engagement signals presents a retention risk profile. Early intervention is significantly more effective than reactive response.`,
    
    'recognition_needed': `${name} has delivered exceptional results over the past ${Math.floor(Math.random() * 6) + 4} weeks including outstanding project delivery and team collaboration. This contribution warrants explicit executive acknowledgment to reinforce the behavior and signal organizational appreciation.`,
    
    'role_clarity': `${name}'s task assignment patterns suggest overlapping or unclear role boundaries with other team members. Clarifying ownership and lane responsibility would likely improve both their performance and team coordination.`,
    
    'succession_planning': `${name} demonstrates the cognitive profile, execution consistency, and organizational influence patterns of a future leader. Recommend adding them to the formal succession planning pipeline.`,
    
    'urgent_wellbeing': `${name} requires immediate leadership attention. Multiple risk indicators are converging — please review their current status before their next work cycle begins.`,
    
    'other': '',
    
    'exceptional_execution': `${name} has maintained a ${onTimeRate}% on-time completion rate over the past ${Math.floor(Math.random() * 6) + 4} weeks, consistently delivering above the team average. This level of execution warrants executive acknowledgment and may inform upcoming recognition or advancement conversations.`,
    
    'leadership_emergence': `${name} is demonstrating leadership behaviors that exceed their current ${currentLevel} designation — including cross-lane coordination, proactive problem escalation, and team influence patterns. Data suggests they are operating at ${nextLevel} capacity and warrant a formal development conversation.`,
    
    'culture_champion': `${name}'s presence in collaboration data, team health patterns, and cross-lane engagement suggests a disproportionately positive impact on organizational cohesion. This contribution is often invisible in performance data but is highly valuable and worth explicit executive acknowledgment.`,
    
    'growth_breakthrough': `${name}'s cognitive alignment score has improved from ${la - 15}% to ${la}% over the past ${Math.floor(Math.random() * 6) + 4} weeks, representing a significant positive shift in lane fit and level-appropriate work. This improvement is worth noting as a development milestone and potential indicator of readiness for expanded responsibility.`,
    
    'cross_lane_excellence': `${name} has been contributing meaningfully across ${Math.floor(Math.random() * 2) + 2} cognitive lanes beyond their primary ${primaryLane} designation. This cognitive range is rare and valuable — worth understanding whether this reflects natural growth, role evolution, or an opportunity to formally expand their lane profile.`,
    
    'client_partner_impact': `${name} played a direct role in advancing key partnerships, contributing to an external outcome that elevated organizational relationships and revenue potential. This impact warrants executive visibility and formal recognition.`
  };
  
  return templates[reason] || '';
};

// ================================================
// RECIPIENTS LIST
// ================================================
const RECIPIENTS = [
  { id: 'ceo', name: 'CEO', initials: 'CE', color: FLAG_COLORS.teal, role: 'Chief Executive Officer', defaultConcern: true, defaultPositive: true },
  { id: 'cos', name: 'Chief of Staff', initials: 'CS', color: FLAG_COLORS.purple, role: 'Executive Office', defaultConcern: true, defaultPositive: false },
  { id: 'national_director', name: 'National Director', initials: 'ND', color: FLAG_COLORS.teal, role: 'National Operations', icon: Globe },
  { id: 'regional_east', name: 'Regional Director East', initials: 'RE', color: FLAG_COLORS.teal, role: 'East Region', icon: MapPin },
  { id: 'regional_west', name: 'Regional Director West', initials: 'RW', color: FLAG_COLORS.teal, role: 'West Region', icon: MapPin },
  { id: 'hr_director', name: 'HR Director', initials: 'HR', color: FLAG_COLORS.gray, role: 'Human Resources', icon: Users },
  { id: 'direct_manager', name: 'Direct Manager', initials: 'DM', color: FLAG_COLORS.teal, role: 'Direct Report Chain', icon: User, defaultPositive: true },
  { id: 'soul_food', name: 'Soul Food Director', initials: 'SF', color: FLAG_COLORS.amber, role: 'Soul Food Division', soulFoodOnly: true },
  { id: 'exec_team', name: 'Executive Team', initials: 'ET', color: FLAG_COLORS.purple, role: 'All Directors + C-Suite', isGroup: true },
  { id: 'board', name: 'Board Observer', initials: 'BO', color: FLAG_COLORS.gray, role: 'Board-Level Visibility', isPremium: true, icon: Lock }
];

// ================================================
// RECIPIENT PILL COMPONENT
// ================================================
const RecipientPill = ({ recipient, isSelected, onToggle }) => {
  const Icon = recipient.icon;
  
  return (
    <button
      onClick={onToggle}
      className={`flex items-center gap-2 px-3 py-2 rounded-xl transition-all ${
        isSelected 
          ? 'text-white shadow-md' 
          : 'border-2 hover:border-gray-300'
      }`}
      style={{
        backgroundColor: isSelected ? recipient.color : 'transparent',
        borderColor: isSelected ? recipient.color : '#E5E7EB'
      }}
      title={`${recipient.name} — ${recipient.role}`}
    >
      {recipient.isGroup ? (
        <div className="flex -space-x-1.5">
          <div className="w-5 h-5 rounded-full bg-white/30 flex items-center justify-center text-[8px] font-bold">C</div>
          <div className="w-5 h-5 rounded-full bg-white/30 flex items-center justify-center text-[8px] font-bold">S</div>
          <div className="w-5 h-5 rounded-full bg-white/30 flex items-center justify-center text-[8px] font-bold">D</div>
        </div>
      ) : (
        <div 
          className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
            isSelected ? 'bg-white/30' : ''
          }`}
          style={{ backgroundColor: isSelected ? 'rgba(255,255,255,0.3)' : `${recipient.color}20`, color: isSelected ? 'white' : recipient.color }}
        >
          {Icon ? <Icon className="w-3 h-3" /> : recipient.initials}
        </div>
      )}
      <span className={`text-sm font-medium ${isSelected ? '' : 'text-gray-700'}`}>
        {recipient.name}
      </span>
      {recipient.isPremium && (
        <Lock className={`w-3 h-3 ${isSelected ? 'text-white/70' : 'text-gray-400'}`} />
      )}
    </button>
  );
};

// ================================================
// ENHANCED FLAG FOR REVIEW MODAL
// ================================================
const EnhancedFlagModal = ({ isOpen, onClose, member, brief, onFlagComplete }) => {
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [reason, setReason] = useState('');
  const [flagNote, setFlagNote] = useState('');
  const [urgency, setUrgency] = useState('attention');
  const [priorityInReview, setPriorityInReview] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  // Determine if this is a positive flag
  const isPositiveFlag = useMemo(() => {
    return POSITIVE_FLAGS.some(f => f.id === reason);
  }, [reason]);

  // Get colors based on flag type
  const accentColor = isPositiveFlag ? FLAG_COLORS.teal : FLAG_COLORS.amber;
  const accentLight = isPositiveFlag ? FLAG_COLORS.tealLight : FLAG_COLORS.amberLight;
  const accentDark = isPositiveFlag ? FLAG_COLORS.tealDark : FLAG_COLORS.amberDark;

  // Reset and set default recipients when modal opens
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setFlagNote('');
      setUrgency('attention');
      setPriorityInReview(true);
      setSuccess(false);
      setShowError(false);
      // Default concern recipients
      setSelectedRecipients(['ceo', 'cos']);
    }
  }, [isOpen]);

  // Update recipients when switching between concern/positive
  useEffect(() => {
    if (isPositiveFlag) {
      setSelectedRecipients(['ceo', 'direct_manager']);
    } else if (reason) {
      setSelectedRecipients(['ceo', 'cos']);
    }
  }, [isPositiveFlag, reason]);

  // Auto-populate flag note when reason changes
  useEffect(() => {
    if (reason && reason !== 'other') {
      const note = generateFlagNote(reason, member, brief);
      setFlagNote(note);
    } else if (reason === 'other') {
      setFlagNote('');
    }
  }, [reason, member, brief]);

  const toggleRecipient = (recipientId) => {
    setSelectedRecipients(prev => {
      if (recipientId === 'exec_team') {
        // Toggle all executives
        const execIds = ['ceo', 'cos', 'national_director', 'regional_east', 'regional_west'];
        const allSelected = execIds.every(id => prev.includes(id));
        if (allSelected) {
          return prev.filter(id => !execIds.includes(id));
        } else {
          return [...new Set([...prev, ...execIds])];
        }
      }
      if (prev.includes(recipientId)) {
        return prev.filter(id => id !== recipientId);
      }
      return [...prev, recipientId];
    });
  };

  const handleSubmit = async () => {
    if (!reason) {
      setShowError(true);
      return;
    }
    if (flagNote.length < 20) {
      return;
    }
    
    setSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    setSubmitting(false);
    setSuccess(true);
    
    setTimeout(() => {
      setSuccess(false);
      onClose();
      onFlagComplete?.();
    }, 3000);
  };

  if (!isOpen) return null;

  const primaryLane = brief?.sections?.cognitiveStatus?.primaryLane;
  const charCount = flagNote.length;
  const isNoteValid = charCount >= 20;

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-hidden flex flex-col" 
        data-testid="flag-review-modal"
      >
        {success ? (
          <div className="p-12 text-center">
            <div 
              className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center"
              style={{ backgroundColor: accentLight }}
            >
              {isPositiveFlag ? (
                <Star className="w-10 h-10" style={{ color: accentColor }} />
              ) : (
                <Check className="w-10 h-10" style={{ color: accentColor }} />
              )}
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-2">
              {isPositiveFlag ? 'Recognition Flag Submitted' : 'Flagged for Executive Review'}
            </h3>
            <p className="text-gray-500 mb-4">
              {isPositiveFlag ? (
                <>The selected recipients have been notified.<br/>{member?.name} will receive a recognition acknowledgment.</>
              ) : (
                <>The selected recipients have been notified.<br/>This item will appear in their Executive Review pipeline.</>
              )}
            </p>
            {!isPositiveFlag && (
              <button 
                className="font-medium hover:underline flex items-center gap-1 mx-auto"
                style={{ color: accentColor }}
              >
                View in Insights <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        ) : (
          <>
            {/* Header - Dynamic color based on flag type */}
            <div 
              className="p-6 border-b"
              style={{ backgroundColor: accentLight, borderColor: accentColor + '40' }}
            >
              <div className="flex items-center gap-3">
                {isPositiveFlag ? (
                  <Award className="w-6 h-6" style={{ color: accentColor }} />
                ) : (
                  <Flag className="w-6 h-6" style={{ color: accentColor }} />
                )}
                <h3 className="text-xl font-bold" style={{ color: accentDark }}>
                  {isPositiveFlag ? 'Flag for Recognition' : 'Flag for Executive Review'}
                </h3>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 space-y-5">
              {/* Send To - Multi-select recipients */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Send to</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {RECIPIENTS.map(recipient => (
                    <RecipientPill
                      key={recipient.id}
                      recipient={recipient}
                      isSelected={selectedRecipients.includes(recipient.id)}
                      onToggle={() => toggleRecipient(recipient.id)}
                    />
                  ))}
                </div>
                <p className="text-xs text-gray-500">
                  Selected recipients will receive a notification and this flag will appear in their Executive Review pipeline.
                </p>
              </div>
              
              {/* Flagged Person */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Flagging</label>
                <div 
                  className="flex items-center gap-4 p-4 rounded-xl border-2"
                  style={{ borderColor: accentColor + '40', backgroundColor: accentLight }}
                >
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: primaryLane?.color || '#6B7280' }}
                  >
                    {member?.avatar || member?.name?.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <p className="font-bold text-gray-900">{member?.name}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span 
                        className="px-2 py-0.5 rounded text-xs font-medium"
                        style={{ backgroundColor: `${primaryLane?.color}20`, color: primaryLane?.color }}
                      >
                        {primaryLane?.name}
                      </span>
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                        {brief?.member?.primaryLevel}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Reason for Flagging - Grouped dropdown */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Reason for flagging <span className="text-red-500">*</span>
                </label>
                <select 
                  value={reason} 
                  onChange={e => {
                    setReason(e.target.value);
                    setShowError(false);
                  }}
                  className={`w-full p-3 rounded-xl border-2 focus:ring-1 outline-none transition-colors ${
                    showError && !reason 
                      ? 'border-amber-500 focus:border-amber-500 focus:ring-amber-500' 
                      : 'border-gray-200 focus:border-gray-400 focus:ring-gray-400'
                  }`}
                  data-testid="reason-dropdown"
                >
                  <option value="">Select a reason...</option>
                  <optgroup label="CONCERN FLAGS">
                    {CONCERN_FLAGS.map(f => (
                      <option key={f.id} value={f.id}>{f.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label="POSITIVE FLAGS">
                    {POSITIVE_FLAGS.map(f => (
                      <option key={f.id} value={f.id}>{f.label}</option>
                    ))}
                  </optgroup>
                </select>
                {showError && !reason && (
                  <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Please select a reason for flagging
                  </p>
                )}
              </div>
              
              {/* Flag Note with character count */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">Flag note</label>
                <div className="relative">
                  <textarea 
                    value={flagNote} 
                    onChange={e => setFlagNote(e.target.value)} 
                    rows={4}
                    placeholder={reason === 'other' ? 'Add your custom note here...' : ''}
                    className={`w-full p-3 rounded-xl border focus:ring-1 outline-none resize-none transition-colors ${
                      flagNote.length > 0 && !isNoteValid
                        ? 'border-amber-300 focus:border-amber-500 focus:ring-amber-500'
                        : 'border-gray-200 focus:border-gray-400 focus:ring-gray-400'
                    }`}
                  />
                  <div className="absolute bottom-2 right-3 flex items-center gap-2">
                    <span className={`text-xs ${isNoteValid ? 'text-gray-400' : 'text-amber-500'}`}>
                      {charCount}/20 min
                    </span>
                  </div>
                </div>
                {!isNoteValid && charCount > 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    Add context to help recipients act quickly.
                  </p>
                )}
              </div>
              
              {/* Urgency - Only show for concern flags */}
              {!isPositiveFlag && (
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">Urgency</label>
                  <div className="flex gap-2">
                    {[
                      { id: 'routine', label: 'Routine', color: 'gray' },
                      { id: 'attention', label: 'Needs Attention', color: 'amber' },
                      { id: 'urgent', label: 'Urgent', color: 'red' }
                    ].map(u => (
                      <button 
                        key={u.id} 
                        onClick={() => setUrgency(u.id)}
                        className={`flex-1 py-2.5 rounded-xl font-medium text-sm transition-all ${
                          urgency === u.id 
                            ? u.color === 'amber' ? 'bg-amber-500 text-white' 
                              : u.color === 'red' ? 'bg-red-500 text-white' 
                              : 'bg-gray-500 text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {u.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Priority Checkbox */}
              <label className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={priorityInReview} 
                  onChange={e => setPriorityInReview(e.target.checked)}
                  className="w-4 h-4 rounded text-amber-500 focus:ring-amber-500"
                  style={{ accentColor: accentColor }}
                />
                <span className="text-sm text-gray-700">
                  {isPositiveFlag ? 'Include in next recognition cycle' : 'Priority in next executive review'}
                </span>
              </label>
            </div>
            
            {/* Footer */}
            <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
              <button 
                onClick={onClose}
                className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-100"
              >
                Cancel
              </button>
              <button 
                onClick={handleSubmit} 
                disabled={submitting || !isNoteValid}
                className="px-5 py-2.5 rounded-xl font-medium flex items-center gap-2 disabled:opacity-50 transition-colors"
                style={{ 
                  backgroundColor: accentColor, 
                  color: isPositiveFlag ? '#1A1A1A' : 'white' 
                }}
                data-testid="submit-flag-btn"
              >
                {submitting ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : isPositiveFlag ? (
                  <Star className="w-4 h-4" />
                ) : (
                  <Flag className="w-4 h-4" />
                )}
                {isPositiveFlag ? 'Submit Recognition Flag' : 'Submit Flag'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EnhancedFlagModal;
